import React, { useEffect, useState } from 'react';
import { ReactSVG } from 'react-svg';

import { Button, Col, Form, Modal, Row, Spin } from 'antd';
import BreadCrumb from '../../modules/breadcrumbs';
import LocationInfo from '../Elements/LocationInfo';
import PricingInfo from '../Elements/PricingInfo';
import WebhooksInfo from '../Elements/WebhooksInfo';
import CompanyForm from '../Forms/Companies/Company';
import LocationForm from '../Forms/Companies/Location';
import FormTitleHeader from '../Header/FormTitleHeader';
import './NewCompany.scss';

import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom/cjs/react-router-dom';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { v4 as uuidv4 } from 'uuid';
import { ROLES } from '../../constants';
import { doCreateCompany, doGetCompanies, doGetCompany, doUpdateCompany } from '../../modules/companies/stores/thunk';
import { doCreateLocation } from '../../modules/locations/stores/thunk';
import { selectAllDeliveryAreas } from '../../modules/delivery-areas/stores/slice';
import { doGetAllDeliveryAreas } from '../../modules/delivery-areas/stores/thunk';
import {
  doCreatePricing,
  doDeletePricing,
  doDeletePricingRange,
  doGetPricing,
  doUpdatePricing,
} from '../../modules/pricing/stores/thunk';
import { selectAllUsers } from '../../modules/users/store/slice';
import { doGetUsers } from '../../modules/users/store/thunk';
import {
  doCreateWebhook,
  doDeleteWebhook,
  doGetAllWebhooks,
  doUpdateWebhook,
} from '../../modules/webhooks/stores/thunk';
import { convertModelToFormData } from '../../utils/common';
import PricingForm from '../Forms/Companies/Pricing';
import WebhooksForm from '../Forms/Companies/Webhooks';
import usePageView from '../../hooks/usePageView';

const CompanyPage = () => {
  const dispatch = useDispatch();
  let { id } = useParams();
  const history = useHistory();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [locations, setLocations] = useState([]);
  const [locationDetail, setLocationDetail] = useState();
  const [fileList, setFileList] = useState([]);
  const [pricingDetail, setPricingDetail] = useState();
  const [pricing, setPricing] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [webhooksDetail, setWebhooksDetail] = useState([]);
  const [isPricingModalOpen, setPricingModalOpen] = useState(false);
  const [isWebhooksModalOpen, setWebhooksModalOpen] = useState(false);
  const [form] = Form.useForm();

  const breadCrumbs = [{ text: 'COMPANIES', url: `/companies` }, { text: id ? id : 'CREATE NEW COMPANY' }];
  const isFetching = useSelector((state) => state.company.isFetching);
  const action = useSelector((state) => state.company.action);
  const status = useSelector((state) => state.company.status);
  const company = useSelector((state) => state.company.company);
  const whooks = useSelector((state) => state.webhooks);
  const prcng = useSelector((state) => state.pricing);
  const users = useSelector((state) => selectAllUsers(state));
  const deliveryAreas = useSelector((state) => selectAllDeliveryAreas(state));

  // Emit page view event
  usePageView(window.location.href);

  const onClickEditLocation = (local) => {
    const location = locations.find((l) => l.id === local.id || l.idNew === local.idNew);
    setLocationDetail(location);
    setIsModalOpen(true);
  };

  const onClickEditPricing = (local) => {
    const priceProfile = pricing.find(
      (p) => (local.id && p.id === local.id) || (local.idNew && p.idNew === local.idNew),
    );
    setPricingDetail(priceProfile);
    setPricingModalOpen(true);
  };

  const onClickEditWebhooks = (local) => {
    setWebhooksDetail(local);
    setWebhooksModalOpen(true);
  };

  const onClickDeleteWebhooks = async (local) => {
    const updatedWebhooks = webhooks.filter((w) => w.id !== local.id || w.idNew !== local.idNew);
    setWebhooks(updatedWebhooks);
    // Delete webhook from db
    if (local.id) {
      await dispatch(doDeleteWebhook(local));
      await dispatch(
        doGetAllWebhooks({
          companyId: id,
        }),
      );
    }
  };

  const onClickDeletePricing = async (price) => {
    const updatedPricing = pricing.filter((p) => p.id !== price.id || p.idNew !== price.idNew);
    if (price.id) {
      const params = { ...price, companyId: id };
      await dispatch(doDeletePricing(params));
    }
    setPricing(updatedPricing);
  };

  useEffect(() => {
    if (['create', 'update'].includes(action) && status === 'succeeded') {
      history.push(`/companies`);
    }
  }, [isFetching]);

  // Fetch all information
  useEffect(() => {
    if (id) {
      setWebhooks([]);
      setPricing([]);
      dispatch(
        doGetAllWebhooks({
          companyId: id,
        }),
      );
      dispatch(doGetPricing({ companyId: id }));
      dispatch(doGetCompany({ id }));
    }
    dispatch(doGetUsers({ page: 1, take: 1000, roles: [ROLES.COMPANY_OWNER, ROLES.LOCATION_USER] }));
    dispatch(doGetAllDeliveryAreas());
  }, [id]);

  // Add fetched company info to UI
  useEffect(() => {
    if (id && company) {
      form.setFieldsValue({
        name: company.name,
        debtorId: company.debtorId,
        companyOwner: company.users.map((cu) => cu.id),
      });
      if (company.logo) {
        setFileList([
          {
            uid: company.id + 'logo',
            status: 'done',
            url: company.logoUrl,
            name: company.name,
          },
        ]);
      }
    } else {
      form.resetFields();
      setFileList([]);
    }
  }, [company, id]);

  // Add the fetched company webhooks to webhooks array
  useEffect(() => {
    if (id && whooks) {
      const fetchedWebhooks = Object.values(whooks.entities);
      setWebhooks(fetchedWebhooks);
    } else {
      setWebhooks([]);
    }
  }, [whooks, id]);

  // Add the fetched pricing to pricing array
  useEffect(() => {
    if (id && prcng) {
      const fetchedPricing = Object.values(prcng.entities).map((p) => ({
        ...p,
        // start and end date are converted to show them correctly in antd DatePicker
        startDate: p.startDate ? dayjs(p.startDate) : undefined,
        endDate: p.endDate ? dayjs(p.endDate) : undefined,
      }));
      setPricing(fetchedPricing);
    } else {
      setPricing([]);
    }
  }, [prcng, id]);

  const handleSubmitLocation = (values) => {
    const newLocations = [...locations];
    if (!(locationDetail?.id || locationDetail?.idNew)) {
      newLocations.push({ ...values, idNew: uuidv4() });
    } else {
      const index = locations.findIndex((l) => l.id === locationDetail.id || l.idNew === locationDetail.idNew);
      if (index > -1) {
        newLocations.splice(index, 1, {
          ...locations[index],
          ...values,
        });
      }
    }
    setLocations(newLocations);
    cancelModal();
  };

  const handleSubmitPricing = (values) => {
    const newPricing = [...pricing];
    if (!(pricingDetail?.id || pricingDetail?.idNew)) {
      newPricing.push({ ...values, idNew: uuidv4() });
    } else {
      const index = pricing.findIndex(
        (p) =>
          (pricingDetail.id && p.id === pricingDetail.id) || (pricingDetail.idNew && p.idNew === pricingDetail.idNew),
      );
      if (index > -1) {
        newPricing.splice(index, 1, {
          ...pricing[index],
          ...values,
        });
      }
    }
    setPricing(newPricing);
    cancelPricingModal();
  };

  const handleSubmitWebhooks = (values) => {
    const newWebhooks = [...webhooks];
    if (!(webhooksDetail?.id || webhooksDetail?.idNew)) {
      newWebhooks.push({ ...values, idNew: uuidv4() });
    } else {
      const index = webhooks.findIndex(
        (w) =>
          (w.id === webhooksDetail.id && w.id !== undefined) ||
          (w.idNew === webhooksDetail.idNew && w.idNew !== undefined),
      );
      if (index > -1) {
        newWebhooks.splice(index, 1, {
          ...webhooks[index],
          ...values,
        });
      }
    }
    setWebhooks(newWebhooks);
    cancelWebhooksModal();
  };

  const handleDeletePricingRange = async (range, pricingId) => {
    const data = { ...range, companyId: id, pricingId: pricingId };
    await dispatch(doDeletePricingRange(data));
  };

  const onAddLocation = () => {
    setIsModalOpen(true);
  };

  const onAddPricing = () => {
    setPricingModalOpen(true);
  };

  const onAddWebhooks = () => {
    setWebhooksModalOpen(true);
  };

  const cancelModal = () => {
    if (locationDetail) setLocationDetail(undefined);
    setIsModalOpen(false);
  };

  const cancelPricingModal = () => {
    if (pricingDetail) setPricingDetail(undefined);
    setPricingModalOpen(false);
  };

  const cancelWebhooksModal = () => {
    if (webhooksDetail) setWebhooksDetail(undefined);
    setWebhooksModalOpen(false);
  };

  const handleCreateCompany = async (values) => {
    try {
      const logo = values.logo?.length > 0 ? values.logo[0].originFileObj : undefined;
      const params = {
        name: values.name,
        debtorId: values.debtorId,
        userIds: values.companyOwner,
        pricing: [],
      };

      pricing.forEach((p) => {
        params.pricing.push({
          name: p.name,

          minimumDaily: p.dailyMinimum,
          minimumWeekly: p.weeklyMinimum,
          startDate: p.startDate,
          endDate: p.endDate,
          pricingRanges: Object.entries(p.pricingRanges)
            .map(([key, value]) => {
              return value.map((item) => ({
                ...item,

                orderType: key,
              }));
            })
            .flat(),
        });
      });
      const formData = convertModelToFormData({ ...params });
      if (logo) formData.append('logo', logo);
      if (id) {
        formData.append('id', id);
        await dispatch(doUpdateCompany(formData));
        for (const p of pricing) {
          if (p.id) {
            const data = {
              id: p.id,
              companyId: id,
              name: p.name,

              minimumDaily: p.dailyMinimum,
              minimumWeekly: p.weeklyMinimum,
              startDate: p.startDate.format(),
              endDate: p.endDate?.format(),
              pricingRanges: JSON.stringify(
                Object.entries(p.pricingRanges)
                  .map(([key, value]) => {
                    return value.map((item) => ({
                      ...item,

                      orderType: key,
                    }));
                  })
                  .flat(),
              ),
            };
            await dispatch(doUpdatePricing(data));
          } else {
            const data = {
              companyId: id,

              name: p.name,
              minimumDaily: p.dailyMinimum,
              minimumWeekly: p.weeklyMinimum,
              startDate: p.startDate.format(),
              endDate: p.endDate?.format(),
              pricingRanges: JSON.stringify(
                Object.entries(p.pricingRanges)
                  .map(([key, value]) => {
                    return value.map((item) => ({
                      ...item,

                      orderType: key,
                    }));
                  })
                  .flat(),
              ),
            };
            await dispatch(doCreatePricing(data));
          }
        }
      } else {
        const res = await dispatch(doCreateCompany(formData));

        // save locations separately
        await Promise.all(
          locations.map(async (l) => {
            l.companyId = res.payload.id;
            delete l.addressLat;
            delete l.addressLng;
            await dispatch(doCreateLocation(l));
          }),
        );
        dispatch(doGetCompanies({ page: 1, take: 1000 }));
      }
    } catch (e) {
      console.log(e);
    }

    // Create, update and remove webhooks
    if (id) {
      if (webhooks) {
        try {
          for (const w of webhooks) {
            if (w.idNew) {
              const data = {
                event: w.event,

                webhookURL: w.webhookURL,
                companyId: company.id,
              };
              await dispatch(doCreateWebhook(data));
            }
            if (w.id) {
              const data = {
                id: w.id,
                event: w.event,

                webhookURL: w.webhookURL,
              };
              await dispatch(doUpdateWebhook(data));
            }
          }
        } catch (e) {
          console.log(e);
        }
      }
    }
  };

  const onClickCreate = () => {
    form.submit();
  };

  return (
    <Spin spinning={isFetching}>
      <div className="Content CompanyPage">
        <BreadCrumb breadCrumbs={breadCrumbs} />

        <div className="FormContent">
          <Row gutter={[24, 24]}>
            <Col span={24}>
              <FormTitleHeader title="Company" />
              <div className="CompanyInfo">
                <CompanyForm
                  form={form}
                  users={users}
                  handleCreateCompany={handleCreateCompany}
                  fileList={fileList}
                  setFileList={setFileList}
                />
              </div>
            </Col>
            {!id && (
              <Col span={24}>
                <>
                  <FormTitleHeader title="Locations" />
                  <div className="CompanyLocations">
                    <div className="location-items">
                      {locations.length > 0 &&
                        locations.map((local) => (
                          <LocationInfo
                            key={local.idNew || local.id}
                            location={local}
                            onClickEdit={() => onClickEditLocation(local)}
                          />
                        ))}
                    </div>
                    <div className="add-location">
                      <Button className="CreateButton ButtonWhite" onClick={onAddLocation}>
                        Add Location
                        <ReactSVG className="icon" src="/icons/plus.svg" />
                      </Button>
                    </div>
                  </div>
                </>
              </Col>
            )}
            <Col span={24}>
              <>
                <FormTitleHeader title="Pricing" />
                <div className="CompanyPricing">
                  <div className="pricing-items">
                    {pricing.length > 0 &&
                      pricing.map((p) => (
                        <PricingInfo
                          key={p.idNew || p.id}
                          pricing={p}
                          onClickEdit={() => onClickEditPricing(p)}
                          onClickDelete={() => onClickDeletePricing(p)}
                        />
                      ))}
                  </div>
                  <div className="add-pricing">
                    <Button className="CreateButton ButtonWhite" onClick={onAddPricing}>
                      Add Pricing
                      <ReactSVG className="icon" src="/icons/plus.svg" />
                    </Button>
                  </div>
                </div>
              </>
            </Col>
          </Row>
          {id && (
            <>
              <FormTitleHeader title="Webhooks" />
              <div className="CompanyWebhooks">
                <Row gutter={[24, 24]}>
                  {webhooks.length > 0 &&
                    webhooks.map((w) => (
                      <Col key={w.idNew || w.id} span={12}>
                        <WebhooksInfo
                          key={w.idNew || w.id}
                          webhook={w}
                          onClickEdit={() => onClickEditWebhooks(w)}
                          onClickDelete={() => onClickDeleteWebhooks(w)}
                        />
                      </Col>
                    ))}
                </Row>
                <div className="add-webhooks">
                  <Button className="CreateButton ButtonWhite" onClick={onAddWebhooks}>
                    Add Webhooks
                    <ReactSVG className="icon" src="/icons/plus.svg" />
                  </Button>
                </div>
              </div>
            </>
          )}
          <div className="FormFooter">
            <Link to={`/companies`}>
              <Button className="ButtonCancel">Cancel</Button>
            </Link>
            <Button className="CreateButton ButtonPrimary" type="primary" onClick={onClickCreate}>
              {id ? 'Update' : 'Create'}
              <ReactSVG className="icon" src="/icons/done.svg" />
            </Button>
          </div>
        </div>
        {!id && (
          <Modal width={552} closeIcon={false} title="Add Location" open={isModalOpen} forceRender>
            <LocationForm
              location={locationDetail}
              handleCancel={cancelModal}
              handleOk={handleSubmitLocation}
              isModalOpen={isModalOpen}
              isFetching={isFetching}
              users={users}
              deliveryAreas={deliveryAreas}
            />
          </Modal>
        )}
        <Modal width={552} closeIcon={false} title="Add Pricing" open={isPricingModalOpen} forceRender>
          <PricingForm
            pricing={pricingDetail}
            handleCancel={cancelPricingModal}
            handleOk={handleSubmitPricing}
            handleDelete={handleDeletePricingRange}
            isModalOpen={isPricingModalOpen}
            isFetching={isFetching}
          />
        </Modal>
        {id && (
          <Modal width={552} closeIcon={false} title="Add Webhooks" open={isWebhooksModalOpen} forceRender>
            <WebhooksForm
              webhook={webhooksDetail}
              handleCancel={cancelWebhooksModal}
              handleOk={handleSubmitWebhooks}
              isModalOpen={isWebhooksModalOpen}
              isFetching={isFetching}
            />
          </Modal>
        )}
      </div>
    </Spin>
  );
};

export default CompanyPage;
